import {FC, ReactNode} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import CircularProgress from "@mui/material/CircularProgress";
import {Stack} from "@mui/material";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import type {Dayjs} from "dayjs";
import {CourseEnrollmentDueDateSection} from "./CourseEnrollmentDueDateSection";

const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

export interface CourseEnrollmentDueDateDialogProps {
	open: boolean;
	onClose: () => void;
	onApply: () => void;
	title: ReactNode;
	description: ReactNode;
	showDueDateSection: boolean;
	dueDate: Dayjs | null;
	onDueDateChange: (value: Dayjs | null) => void;
	isPending?: boolean;
	titleId?: string;
	descriptionId?: string;
}

export const CourseEnrollmentDueDateDialog: FC<CourseEnrollmentDueDateDialogProps> = ({
	open,
	onClose,
	onApply,
	title,
	description,
	showDueDateSection,
	dueDate,
	onDueDateChange,
	isPending = false,
	titleId = "course-enrollment-dialog-title",
	descriptionId = "course-enrollment-dialog-description"
}) => {
	const {t} = useSSTranslation();

	return (
		<Dialog
			open={open}
			onClose={(_event, reason) => {
				if (reason !== "backdropClick") {
					onClose();
				}
			}}
			aria-labelledby={titleId}
			aria-describedby={descriptionId}
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle id={titleId}>{title}</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<DialogContentText id={descriptionId} variant="body1" color="text.primary">
						{description}
					</DialogContentText>
					{showDueDateSection && (
						<CourseEnrollmentDueDateSection
							dueDate={dueDate}
							onDueDateChange={onDueDateChange}
							disabled={isPending}
						/>
					)}
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose} sx={buttonSx} disabled={isPending}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button
					variant="text"
					onClick={onApply}
					sx={buttonSx}
					disabled={isPending}
					startIcon={isPending ? <CircularProgress size={16} /> : undefined}
				>
					{isPending ? t("common_enrolling", "Enrolling...") : t("common_apply", "Apply")}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
